import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';
import { getBranches, getUserWaiterSalesHalfDayReport, formatDateForApi } from '../../utilities/api';
import useUser from '../../hooks/useUser';
import './billreports.css';

const ALL_BRANCHES_OPTION = { branch_id: 'All', branch_name: 'All Branches' };
const EMPTY_HALF = { total_sales: '0.00', bill_count: 0, payment_modes: {} };

const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

// Running accumulator for a half-day bucket — total_sales/payment_modes kept as numbers
// while summing, formatted back to strings only for display.
const emptyAgg = () => ({ total_sales: 0, bill_count: 0, payment_modes: {} });

const addInto = (acc, half) => {
    const h = half || EMPTY_HALF;
    acc.total_sales += parseFloat(h.total_sales || 0);
    acc.bill_count += Number(h.bill_count || 0);
    Object.entries(h.payment_modes || {}).forEach(([mode, amt]) => {
        acc.payment_modes[mode] = (acc.payment_modes[mode] || 0) + parseFloat(amt || 0);
    });
};

// waiter_no 0/null/'' is the sentinel for "no waiter tagged" (billed straight to the cashier's
// own login) — not a real waiter (see BillReportsContent's getBilledByName/billedByBodyTemplate,
// which uses the same convention).
const hasNoWaiter = (waiterNo) => waiterNo === null || waiterNo === undefined || String(waiterNo).trim() === '' || Number(waiterNo) === 0;

const LEVEL_COLORS = { day: 'var(--primary-color, #2a78d6)', user: '#2a78d6', waiter: '#1baf7a', mode: '#eda100' };
const LEVEL_ICONS = { day: 'pi-calendar', user: 'pi-user', waiter: 'pi-users', mode: 'pi-wallet' };

/**
 * "Sales By Waiters" — a tree per day, columns First Half (06:00-11:59) / Second Half
 * (12:00-23:59) / Total. Each day's total drills down: User (the app login/cashier,
 * payments_details.UserId) -> Waiter (billed under that login, product_billdetail.waiter_no) ->
 * Payment Mode (payments_details.payment_type) — each waiter's bills are billed under a
 * specific user login, so Waiter nests inside User rather than sitting alongside it.
 *
 * Role behavior mirrors the Session Sales page: Manager gets no filters and sees today only
 * (fully expanded); Admin gets a Branch filter and also sees today only (fully expanded);
 * Operator gets a Branch filter and sees today + yesterday (today auto-expanded, yesterday left
 * collapsed); CEO gets manual From/To date pickers + Branch filter + Apply, defaulting to today
 * until applied.
 */
const SalesByWaitersContent = () => {
    const { userRole, ubranchid, ubranch } = useUser();
    const rolename = (userRole || localStorage.getItem('role') || '').toUpperCase();
    const isManager = rolename === 'MANAGER';
    const isAdmin = rolename === 'ADMIN';
    const isOperator = rolename === 'OPERATOR';
    const hasDateFilters = !isManager && !isAdmin && !isOperator;
    const autoFetchRole = isManager || isAdmin || isOperator;

    const [fromDate, setFromDate] = useState(() => {
        if (isOperator) {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return yesterday;
        }
        return new Date();
    });
    const [toDate, setToDate] = useState(new Date());
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [apiRows, setApiRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState({});

    const todayStr = useMemo(() => moment().format('DD-MM-YYYY'), []);
    const yesterdayStr = useMemo(() => moment().subtract(1, 'day').format('DD-MM-YYYY'), []);

    // Branch list — Manager is locked to their own branch, Admin/CEO can pick or see All.
    useEffect(() => {
        const clientId = localStorage.getItem('clientid');
        if (isManager) {
            const branchId = ubranchid || localStorage.getItem('branchid') || '';
            const branchName = ubranch || localStorage.getItem('branch') || branchId;
            const managerBranch = { branch_id: branchId, branch_name: branchName };
            setBranches([managerBranch]);
            setSelectedBranch(managerBranch);
            return;
        }
        if (!clientId) return;
        getBranches(clientId)
            .then((list) => {
                const withAll = [ALL_BRANCHES_OPTION, ...(Array.isArray(list) ? list : [])];
                setBranches(withAll);
                setSelectedBranch(withAll[0]);
            })
            .catch(() => {
                setBranches([ALL_BRANCHES_OPTION]);
                setSelectedBranch(ALL_BRANCHES_OPTION);
            });
    }, [isManager, ubranchid, ubranch]);

    const buildPayload = useCallback(() => {
        const branchId = selectedBranch?.branch_id || 'All';
        const payload = {
            from_date: fromDate ? formatDateForApi(fromDate) : '',
            to_date: toDate ? formatDateForApi(toDate) : '',
            branch_id: branchId
        };
        if (String(branchId).toLowerCase() === 'all') {
            const clientId = localStorage.getItem('clientid');
            if (clientId) payload.client_id = clientId;
        }
        return payload;
    }, [selectedBranch, fromDate, toDate]);

    const fetchReport = useCallback(async () => {
        if (!selectedBranch || !fromDate || !toDate) return;
        setLoading(true);
        try {
            const res = await getUserWaiterSalesHalfDayReport(buildPayload());
            setApiRows(res?.status === 'success' && Array.isArray(res?.data) ? res.data : []);
        } catch (error) {
            console.error('Error loading Sales By Waiters report:', error);
            setApiRows([]);
        } finally {
            setLoading(false);
        }
    }, [selectedBranch, fromDate, toDate, buildPayload]);

    // Manager/Admin have no Apply button — auto-fetch once the branch resolves (and again if
    // Admin changes branch). CEO must click Apply themselves before any data is fetched.
    useEffect(() => {
        if (autoFetchRole && selectedBranch && fromDate && toDate) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFetchRole, selectedBranch]);

    const leafData = (label, level, first, second, total, extra) => ({ label, level, first_half: first, second_half: second, total, ...extra });

    const dayLabel = (dateKey) => {
        if (dateKey === todayStr) return 'Present Day';
        if (dateKey === yesterdayStr) return 'Previous Day';
        return dateKey;
    };

    // Builds Day -> User -> Waiter -> Payment Mode. The API already returns one record per
    // (user, waiter, date) with each half's payment-mode split, so User/Day totals are summed
    // up from those records rather than fetched separately.
    const treeNodes = useMemo(() => {
        const rowsByDate = {};
        (apiRows || []).forEach((r) => {
            (rowsByDate[r.payment_date] ||= []).push(r);
        });

        const dates = Object.keys(rowsByDate).sort((a, b) => {
            const da = moment(a, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
            const db = moment(b, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
            return db.valueOf() - da.valueOf();
        });

        return dates.map((date) => {
            const dayRows = rowsByDate[date] || [];
            const dayAgg = { first_half: emptyAgg(), second_half: emptyAgg(), total: emptyAgg() };

            const rowsByUser = {};
            dayRows.forEach((r) => {
                addInto(dayAgg.first_half, r.first_half);
                addInto(dayAgg.second_half, r.second_half);
                addInto(dayAgg.total, r.total);
                (rowsByUser[r.user_id] ||= { username: r.username, rows: [] }).rows.push(r);
            });

            const userKeys = Object.keys(rowsByUser).sort((a, b) => String(rowsByUser[a].username || '').localeCompare(String(rowsByUser[b].username || '')));

            const userNodes = userKeys.map((userId) => {
                const { username, rows } = rowsByUser[userId];
                const userAgg = { first_half: emptyAgg(), second_half: emptyAgg(), total: emptyAgg() };
                rows.forEach((r) => {
                    addInto(userAgg.first_half, r.first_half);
                    addInto(userAgg.second_half, r.second_half);
                    addInto(userAgg.total, r.total);
                });

                // A row with no waiter tagged (waiter_no 0) isn't a distinct waiter — skip the
                // redundant Waiter row and attach its payment modes straight under the User
                // instead.
                const waiterNodes = rows
                    .slice()
                    .sort((a, b) => String(a.waiter_name || '').localeCompare(String(b.waiter_name || '')))
                    .flatMap((r, i) => {
                        const modeNames = Array.from(new Set([...Object.keys(r.first_half?.payment_modes || {}), ...Object.keys(r.second_half?.payment_modes || {}), ...Object.keys(r.total?.payment_modes || {})])).sort();
                        const modeNodes = modeNames.map((mode) => ({
                            key: `${date}-${userId}-${r.waiter_no}-m-${mode}-${i}`,
                            data: leafData(
                                mode,
                                'mode',
                                { total_sales: r.first_half?.payment_modes?.[mode] || 0 },
                                { total_sales: r.second_half?.payment_modes?.[mode] || 0 },
                                { total_sales: r.total?.payment_modes?.[mode] || 0 },
                                {}
                            )
                        }));
                        if (hasNoWaiter(r.waiter_no)) return modeNodes;
                        return [
                            {
                                key: `${date}-${userId}-w-${r.waiter_no}-${i}`,
                                data: leafData(r.waiter_name, 'waiter', r.first_half || EMPTY_HALF, r.second_half || EMPTY_HALF, r.total || EMPTY_HALF, {}),
                                children: modeNodes
                            }
                        ];
                    });

                return {
                    key: `${date}-u-${userId}`,
                    data: leafData(username, 'user', userAgg.first_half, userAgg.second_half, userAgg.total, {}),
                    children: waiterNodes
                };
            });

            return {
                key: date,
                data: leafData(dayLabel(date), 'day', dayAgg.first_half, dayAgg.second_half, dayAgg.total, {}),
                children: userNodes
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiRows, todayStr, yesterdayStr]);

    // Date rows start collapsed for CEO — the user expands a day manually to drill into
    // User/Waiter/Payment Mode. Manager and Admin only ever see today's tree, so it's expanded
    // fully by default. Operator sees today + yesterday (dates sorted newest-first, so
    // treeNodes[0] is today) — auto-expand only today's details and leave yesterday's collapsed.
    useEffect(() => {
        if (!autoFetchRole) return;
        if (!treeNodes.length) return;
        const keys = {};
        const expandSubtree = (node) => {
            if (node.children && node.children.length) {
                keys[node.key] = true;
                node.children.forEach(expandSubtree);
            }
        };
        if (isOperator) {
            expandSubtree(treeNodes[0]);
        } else {
            treeNodes.forEach(expandSubtree);
        }
        setExpandedKeys(keys);
    }, [autoFetchRole, isOperator, treeNodes]);

    const totalUsers = useMemo(() => new Set((apiRows || []).map((r) => r.user_id)).size, [apiRows]);
    const totalWaiters = useMemo(() => new Set((apiRows || []).map((r) => r.waiter_no)).size, [apiRows]);
    const hasData = treeNodes.length > 0;

    const labelBody = (node) => {
        const { label, level } = node.data;
        return (
            <span className={`sbw-tree-label sbw-tree-label-${level}`}>
                <i className={`pi ${LEVEL_ICONS[level]} mr-2`} style={{ color: LEVEL_COLORS[level] }} />
                {label || 'Unassigned'}
            </span>
        );
    };

    const amountBody = (field) => (node) => {
        const half = node.data[field];
        if (!half) return null;
        return <span className={`sbw-tree-amount sbw-tree-amount-${node.data.level}`}>₹{formatCurrency(half.total_sales)}</span>;
    };

    // PrimeReact's TreeTable spreads rowClassName's return value as a classNames-style
    // object ({ className: true }), not a plain string — a string would get spread as
    // numeric-indexed characters instead of applied as a class.
    const rowClassName = (node) => ({ [`sbw-tree-row-${node.data.level}`]: true });

    const description =
        isManager || isAdmin
            ? `Present Day (${todayStr}), split into First Half and Second Half. Expand to drill into the total — by user, then by the waiters billed under that login, then by payment mode.`
            : isOperator
              ? `Present Day (${todayStr}) vs Previous Day (${yesterdayStr}), split into First Half and Second Half. Today is expanded by default — expand Previous Day, or drill deeper, manually.`
              : 'Pick a date range and Branch, then Apply. Split into First Half and Second Half — expand a day to drill into its total by user, then by the waiters billed under that login, then by payment mode.';

    return (
        <div className="bill-reports-container">
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Sales By Waiters
                </h2>
                <p className="text-color-secondary">{description}</p>
            </div>

            {!isManager && (
                <Card className="mb-4 bill-reports-filter-card">
                    <div className="flex flex-wrap align-items-end bill-reports-filter-row" style={{ gap: '1rem' }}>
                        {hasDateFilters && (
                            <>
                                <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                    <label htmlFor="sbw-from">
                                        From Date <span className="text-red-500">*</span>
                                    </label>
                                    <Calendar id="sbw-from" value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                                </div>
                                <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                    <label htmlFor="sbw-to">
                                        To Date <span className="text-red-500">*</span>
                                    </label>
                                    <Calendar id="sbw-to" value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                                </div>
                            </>
                        )}
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                            <label htmlFor="sbw-branch">Branch</label>
                            <Dropdown id="sbw-branch" value={selectedBranch} options={branches} onChange={(e) => setSelectedBranch(e.value)} optionLabel="branch_name" className="w-full" />
                        </div>
                        {hasDateFilters && (
                            <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                                <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={fetchReport} disabled={!fromDate || !toDate || !selectedBranch || loading} loading={loading} />
                            </div>
                        )}
                    </div>
                </Card>
            )}

            <Card className="mb-4 sbw-tree-card">
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Sales Summary</h3>
                    <div className="flex gap-2">
                        {totalUsers > 0 && <Tag value={`${totalUsers} User${totalUsers === 1 ? '' : 's'}`} style={{ background: LEVEL_COLORS.user }} />}
                        {totalWaiters > 0 && <Tag value={`${totalWaiters} Waiter${totalWaiters === 1 ? '' : 's'}`} style={{ background: LEVEL_COLORS.waiter }} />}
                    </div>
                </div>
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <ProgressSpinner />
                    </div>
                ) : !hasData ? (
                    <div className="flex flex-column align-items-center justify-content-center p-5">
                        <i className="pi pi-chart-bar text-5xl text-color-secondary mb-3"></i>
                        <p className="text-color-secondary">{apiRows === null ? 'Select a date range and branch, then Apply.' : 'No sales found for the selected filters.'}</p>
                    </div>
                ) : (
                    <TreeTable
                        value={treeNodes}
                        expandedKeys={expandedKeys}
                        onToggle={(e) => setExpandedKeys(e.value)}
                        rowClassName={rowClassName}
                        className="p-treetable-sm sbw-tree"
                        tableStyle={{ minWidth: '45rem' }}
                    >
                        <Column field="label" header="Day / User / Waiter / Payment Mode" body={labelBody} expander style={{ minWidth: '260px' }} />
                        <Column field="first_half" header="First Half (06:00-11:59)" body={amountBody('first_half')} style={{ textAlign: 'right', justifyContent: 'flex-end', minWidth: '170px' }} />
                        <Column field="second_half" header="Second Half (12:00-23:59)" body={amountBody('second_half')} style={{ textAlign: 'right', justifyContent: 'flex-end', minWidth: '170px' }} />
                        <Column field="total" header="Total" body={amountBody('total')} style={{ textAlign: 'right', justifyContent: 'flex-end', minWidth: '150px' }} />
                    </TreeTable>
                )}
            </Card>
        </div>
    );
};

export default SalesByWaitersContent;
